import { type AuthMode } from '../types';

import { DEFAULT_ALLOWED_HOST_PATTERN } from '../constants';

/**
 * Detect whether the app is inside an iframe from a trusted *.docyrus.app host
 * or running as a standalone page.
 *
 * If we are in an iframe but NOT from a trusted origin, we fall back
 * to standalone mode (graceful degradation).
 */
export function detectAuthMode(
  allowedOrigins?: string[],
  allowedPattern?: RegExp
): AuthMode {
  if (typeof window === 'undefined') return 'standalone';

  // React Native WebView injects this global — treat as embedded iframe mode
  if ((window as any).ReactNativeWebView) return 'iframe';

  let isInIframe = false;

  try {
    isInIframe = window.self !== window.top;
  } catch {
    // Cross-origin iframe throws — so we ARE in an iframe
    isInIframe = true;
  }

  if (!isInIframe) return 'standalone';

  /*
   * We're in an iframe. Check the host origin using multiple strategies:
   * 1. ancestorOrigins (Chrome/Edge — most reliable)
   * 2. document.referrer (may reflect in-iframe navigation, not the parent)
   * 3. If allowedOrigins are explicitly configured, trust the iframe context
   *
   * The actual security is enforced by validating event.origin on every postMessage
   * in IframeAuth, so detection here is best-effort.
   */
  const pattern = allowedPattern ?? DEFAULT_ALLOWED_HOST_PATTERN;

  // Strategy 1: ancestorOrigins (Chromium browsers)

  const ancestors = (window.location as any).ancestorOrigins;

  if (ancestors && ancestors.length > 0) {
    const parentOrigin = ancestors[0];

    if (allowedOrigins?.includes(parentOrigin)) return 'iframe';
    if (pattern.test(parentOrigin)) return 'iframe';
  }

  // Strategy 2: document.referrer
  try {
    const referrerOrigin = new URL(document.referrer).origin;

    if (allowedOrigins?.includes(referrerOrigin)) return 'iframe';
    if (pattern.test(referrerOrigin)) return 'iframe';
  } catch {
    // Invalid or empty referrer
  }

  /*
   * Strategy 3: if allowedOrigins are explicitly configured and we are in an iframe,
   * trust the iframe context. Security is enforced at the postMessage level.
   */
  if (allowedOrigins && allowedOrigins.length > 0) {
    return 'iframe';
  }

  // In iframe but no allowed origins configured — treat as standalone
  return 'standalone';
}
